import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import axios from 'axios';
import {
  Bold,
  Italic,
  Heading2,
  Heading3,
  Quote,
  List,
  ListOrdered,
  Link as LinkIcon,
  Image as ImageIcon,
  Upload,
  Undo2,
  Redo2,
  Strikethrough,
  Code,
  Minus,
  Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ToolbarButton({ active, onClick, title, children, disabled }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      title={title}
      disabled={disabled}
      data-testid={`rte-btn-${title.toLowerCase().replace(/\s+/g, '-')}`}
      className={`w-9 h-9 flex items-center justify-center border transition-colors ${
        active
          ? 'bg-[#E63946] text-white border-[#E63946]'
          : 'bg-white text-[#1A1A2E] border-[#E5E5E5] hover:border-[#E63946] hover:text-[#E63946]'
      } disabled:opacity-40 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );
}

function RichTextEditor({ value, onChange, placeholder = 'Start writing your article...' }) {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        bulletList: { HTMLAttributes: { class: 'rte-bullet-list' } },
        orderedList: { HTMLAttributes: { class: 'rte-ordered-list' } },
        blockquote: { HTMLAttributes: { class: 'rte-blockquote' } }
      }),
      Image.configure({
        HTMLAttributes: { class: 'rte-image' },
        inline: false,
        allowBase64: false
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { class: 'rte-link', rel: 'noopener noreferrer', target: '_blank' }
      }),
      Placeholder.configure({ placeholder })
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // TipTap returns "<p></p>" for empty content — treat as empty string
      onChange(html === '<p></p>' ? '' : html);
    }
  });

  // Sync external value changes (e.g. when loading an existing article into edit form)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || '';
    if (incoming && current !== incoming) {
      editor.commands.setContent(incoming, false);
    }
  }, [value, editor]);

  const promptLink = useCallback(() => {
    if (!editor) return;
    const previous = editor.getAttributes('link').href || '';
    const url = window.prompt('Link URL (leave empty to remove):', previous);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().unsetLink().run();
      return;
    }
    let safeUrl = url.trim();
    if (!/^https?:\/\//i.test(safeUrl) && !safeUrl.startsWith('/')) {
      safeUrl = 'https://' + safeUrl;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: safeUrl }).run();
  }, [editor]);

  const promptImage = useCallback(() => {
    if (!editor) return;
    const url = window.prompt('Image URL:');
    if (!url) return;
    editor.chain().focus().setImage({ src: url.trim() }).run();
  }, [editor]);

  const handleEditorImageUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editor) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await axios.post(`${API}/upload`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const absUrl = data.url.startsWith('http')
        ? data.url
        : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      editor.chain().focus().setImage({ src: absUrl }).run();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Image upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="border border-[#E5E5E5] focus-within:border-[#E63946] transition-colors" data-testid="rich-text-editor">
      <div className="flex flex-wrap items-center gap-1 p-2 border-b border-[#E5E5E5] bg-[#FFF8F0] sticky top-0 z-10">
        <ToolbarButton active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold">
          <Bold size={16} />
        </ToolbarButton>
        <ToolbarButton active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic">
          <Italic size={16} />
        </ToolbarButton>
        <ToolbarButton active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} title="Strikethrough">
          <Strikethrough size={16} />
        </ToolbarButton>
        <ToolbarButton active={editor.isActive('code')} onClick={() => editor.chain().focus().toggleCode().run()} title="Inline code">
          <Code size={16} />
        </ToolbarButton>

        <div className="w-px h-6 bg-[#E5E5E5] mx-1"></div>

        <ToolbarButton active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Heading 2">
          <Heading2 size={16} />
        </ToolbarButton>
        <ToolbarButton active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Heading 3">
          <Heading3 size={16} />
        </ToolbarButton>
        <ToolbarButton active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Quote">
          <Quote size={16} />
        </ToolbarButton>

        <div className="w-px h-6 bg-[#E5E5E5] mx-1"></div>

        <ToolbarButton active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bullet list">
          <List size={16} />
        </ToolbarButton>
        <ToolbarButton active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numbered list">
          <ListOrdered size={16} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Divider">
          <Minus size={16} />
        </ToolbarButton>

        <div className="w-px h-6 bg-[#E5E5E5] mx-1"></div>

        <ToolbarButton active={editor.isActive('link')} onClick={promptLink} title="Insert link">
          <LinkIcon size={16} />
        </ToolbarButton>
        <ToolbarButton onClick={promptImage} title="Insert image by URL">
          <ImageIcon size={16} />
        </ToolbarButton>
        <ToolbarButton
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
          title="Upload image"
        >
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
        </ToolbarButton>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          onChange={handleEditorImageUpload}
          className="hidden"
          data-testid="rte-image-upload-input"
        />

        <div className="flex-1"></div>

        <ToolbarButton disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()} title="Undo">
          <Undo2 size={16} />
        </ToolbarButton>
        <ToolbarButton disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()} title="Redo">
          <Redo2 size={16} />
        </ToolbarButton>
      </div>

      <EditorContent editor={editor} className="rte-content" data-testid="rich-text-editor-area" />
    </div>
  );
}

export default RichTextEditor;
